import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart } from "lucide-react";
import MSABanner from "../components/MSABanner";

const LOGO_URL = "/slsa_logo_color_transparent.png";

const PARK_FEATURES = [
    { name: "Swan Mountains Section", desc: "Flowing transitions inspired by the iconic Swan Range peaks visible from the park.", icon: "mountain" },
    { name: "Pyramid Peak Rail", desc: "A signature steel rail feature named after one of Seeley Lake's most recognizable landmarks.", icon: "rail" },
    { name: "Double Arrow Quarterpipe", desc: "A classic quarterpipe feature with a nod to the legendary Double Arrow ski area.", icon: "ramp" },
    { name: "Seeley Lake Bowl", desc: "The centerpiece — a flowing concrete bowl shaped in the outline of Seeley Lake itself.", icon: "bowl" },
    { name: "Blackfoot Pump Track", desc: "The shoreline-inspired pump track that follows the outer perimeter of the park's layout.", icon: "track" },
    { name: "Community Ledges", desc: "Accessible street-style features welcoming every skill level from first-timers to veterans.", icon: "ledge" },
];

const VALUES = [
    { title: "Free for Everyone", desc: "The park will always be free and open to the public. No memberships, no fees, no barriers." },
    { title: "All Wheels Welcome", desc: "Skateboards, scooters, BMX bikes, rollerblades — if it rolls, you belong here." },
    { title: "Community-Built", desc: "We're a grassroots effort. Every decision involves the community we're building for." },
    { title: "Built to Last", desc: "Modern concrete construction means a park that serves Seeley Lake for generations to come." },
];

const About = () => (
    <div className="pt-16">
        {/* Page Hero */}
        <section
            data-testid="about-hero"
            className="relative py-24 px-4 bg-[#3D2008] overflow-hidden"
            style={{ backgroundImage: "url('https://images.pexels.com/photos/5738357/pexels-photo-5738357.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')", backgroundSize: "cover", backgroundPosition: "center" }}
        >
            <div className="absolute inset-0 bg-[#3D2008]/85" />
            <div className="relative z-10 max-w-7xl mx-auto">
                <span className="font-marker text-[#C9A84C] text-xl -rotate-1 inline-block mb-4">Our Story</span>
                <h1 className="font-anton text-5xl sm:text-7xl lg:text-8xl text-[#F5EDD6] uppercase leading-none mb-4">About SLSA</h1>
                <p className="font-manrope text-[#F5EDD6]/70 text-xl max-w-2xl">
                    A grassroots, community-driven effort to build a lasting skatepark in the heart of Seeley Lake, Montana.
                </p>
            </div>
        </section>

        {/* Who We Are */}
        <section className="py-20 px-4 bg-[#F5EDD6]" data-testid="who-we-are-section">
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div className="relative">
                    <img
                        src={LOGO_URL}
                        alt="SLSA Logo"
                        className="w-64 h-auto mx-auto lg:mx-0 object-contain shadow-[12px_12px_0px_0px_rgba(196,82,42,0.35)]"
                    />
                </div>
                <div>
                    <span className="font-marker text-[#C4522A] text-2xl -rotate-2 inline-block mb-4">Who We Are</span>
                    <h2 className="font-anton text-4xl sm:text-5xl text-[#3D2008] uppercase leading-tight mb-6">
                        Riders, Parents,<br />&amp; Neighbors
                    </h2>
                    <p className="font-manrope text-[#5C4A35] text-lg leading-relaxed mb-5">
                        The Seeley Lake Skatepark Association (SLSA) was formed by a homegrown group of local riders, parents, and neighbors who believe Seeley Lake deserves a world-class skatepark.
                    </p>
                    <p className="font-manrope text-[#5C4A35] text-lg leading-relaxed mb-5">
                        We're energetic, determined, and deeply committed to building something that serves the whole community — now and for generations to come. We work side-by-side with local families, businesses, schools, and community leaders to make this dream a reality.
                    </p>
                    <p className="font-manrope text-[#5C4A35] text-lg leading-relaxed">
                        In a town where many great causes ask for support, SLSA is focused on building something unique: a free, modern, concrete skatepark that will become a source of pride for Seeley Lake.
                    </p>
                </div>
            </div>
        </section>

        {/* Mission */}
        <section className="py-20 px-4 bg-[#3D2008]" data-testid="mission-section">
            <div className="max-w-7xl mx-auto">
                <div className="max-w-3xl">
                    <span className="font-marker text-[#C9A84C] text-2xl -rotate-1 inline-block mb-4">Why We Exist</span>
                    <h2 className="font-anton text-4xl sm:text-6xl text-[#F5EDD6] uppercase leading-tight mb-8">Our Mission</h2>
                    <blockquote className="border-l-4 border-[#C4522A] pl-6 mb-10">
                        <p className="font-manrope text-[#F5EDD6]/80 text-xl leading-relaxed italic">
                            "Seeley Lake Skatepark Association exists to build community support for, fund, and help construct a modern, state-of-the-art skatepark in Seeley Lake. We're creating a safe, inclusive space for skateboarders, scooter riders, rollerbladers, and BMX riders of all ages and abilities."
                        </p>
                    </blockquote>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
                    {VALUES.map((v) => (
                        <div key={v.title} className="bg-[#F5EDD6]/5 border border-[#C9A84C]/30 p-6 hover:bg-[#F5EDD6]/10 transition-colors">
                            <Heart size={24} className="text-[#C4522A] mb-4" />
                            <h3 className="font-anton text-[#C9A84C] uppercase text-lg mb-2">{v.title}</h3>
                            <p className="font-manrope text-[#F5EDD6]/60 text-sm leading-relaxed">{v.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* MSA Fiscal Sponsor Banner — directly after Our Mission */}
        <MSABanner label="Fiscal Sponsor" />

        {/* Park Vision */}
        <section className="py-20 px-4 bg-[#2D5A27]" data-testid="park-design-section">
            <div className="max-w-7xl mx-auto">
                <div className="max-w-3xl mb-16">
                    <span className="font-marker text-[#C9A84C] text-2xl -rotate-1 inline-block mb-4">The Concept</span>
                    <h2 className="font-anton text-4xl sm:text-6xl text-[#F5EDD6] uppercase leading-tight mb-6">
                        Park Design<br /><span className="text-[#C9A84C]">Vision</span>
                    </h2>
                    <p className="font-manrope text-[#F5EDD6]/80 text-xl leading-relaxed mb-6">
                        Our idea is to use Seeley Lake itself and other local landmarks as inspiration for building rideable skatepark features.
                    </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {PARK_FEATURES.map((f, i) => (
                        <div key={f.name}
                            className="bg-[#F5EDD6]/5 border-2 border-[#C9A84C]/30 p-6 hover:border-[#C9A84C] hover:bg-[#F5EDD6]/10 transition-all duration-200"
                            data-testid={`park-feature-${i}`}>
                            <h3 className="font-anton text-[#C9A84C] uppercase text-lg mb-2">{f.name}</h3>
                            <p className="font-manrope text-[#F5EDD6]/60 text-sm leading-relaxed">{f.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* Fiscal Sponsor section removed — SLSA is its own nonprofit */}

        {/* CTA */}
        <section className="py-16 px-4 bg-[#C4522A]">
            <div className="max-w-4xl mx-auto text-center">
                <h2 className="font-anton text-4xl sm:text-6xl text-white uppercase mb-6">Be Part of This</h2>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link to="/donate" data-testid="about-donate-btn"
                        className="px-8 py-4 bg-white text-[#C4522A] font-anton uppercase tracking-wider text-lg border-2 border-[#3D2008] shadow-[4px_4px_0px_0px_#3D2008] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform duration-150">
                        Donate
                    </Link>
                </div>
            </div>
        </section>
    </div>
);

export default About;
